import { Images } from "@/constants/images"
import { t } from "@/languages/i18n"
import { DefaultImage, handleImageError } from "@/lib/external-components"
import { formatPrice } from "@/lib/price-helper"
import { updateImageSrc } from "@/lib/utils"
import { TourDetail } from "@/types/tour"
import Image from "next/image"
import Link from "next/link"

const TourCard = ({ tourDetail }: { tourDetail: TourDetail }) => {
  const img = tourDetail?.feature_img?.[0]
  const imgAlt = typeof img === "object" ? (img as { alternativeText?: string }).alternativeText : tourDetail?.name
  const imageUrl = (() => {
    if (!tourDetail?.feature_img || tourDetail.feature_img.length === 0) {
      return DefaultImage
    }
    const firstImg = typeof tourDetail.feature_img === "string" ? tourDetail.feature_img : tourDetail.feature_img[0]
    if (typeof firstImg === "string") {
      return updateImageSrc(firstImg)
    }
    if (typeof firstImg === "object" && firstImg) {
      const url = (firstImg as { url?: string }).url
      return updateImageSrc(url)
    }
    return DefaultImage
  })()

  const price = tourDetail?.price_overview || "Contact us for price"
  const priceMain = String(price).split("/")?.[0] || price
  const priceUnit = String(price).split("/")?.[1] || t("person")
  const currentLocale = typeof document !== "undefined" ? document.documentElement.lang || "en" : "en"
  const isVI = currentLocale?.toLowerCase().startsWith("vi")
  const slug = isVI ? `/vi/tours/${tourDetail.slug}` : `/tours/${tourDetail.slug}`
  console.log("imageUrl", imageUrl, tourDetail)
  return (
    <Link
      href={slug}
      className="group flex flex-col h-full w-full rounded-2xl overflow-hidden bg-white shadow md:hover:shadow-xl transition-all duration-300"
    >
      <div className="relative flex-shrink-0 overflow-hidden">
        <div className={`w-full h-[229px] ${tourDetail?.is_new ? "bg-[#EB1E12]" : ""}`}>
          <div className={`relative w-full h-full overflow-hidden ${tourDetail?.is_new ? "rounded-tl-[85px]" : ""}`}>
            <Image
              src={imageUrl}
              alt={imgAlt || "Tour image"}
              width={300}
              height={229}
              onError={e => handleImageError(e, typeof img === "string" ? img : (img as { url: string })?.url)}
              className={`w-full h-full object-cover absolute z-[11] transition-transform duration-500 md:group-hover:scale-110 ${
                tourDetail?.is_new ? "rounded-tl-[85px]" : ""
              }`}
            />
          </div>
          {tourDetail?.is_new && (
            <div className="absolute top-0 left-0">
              <div className="bg-[#EB1E12] text-white text-xs font-bold flex items-center justify-center relative w-[135px] h-10 -rotate-45 -mt-0.5 -ml-12 z-10 shadow-md">
                <span className="relative z-10">{t("New")}</span>
                <div className="absolute right-0 top-0 w-0 h-0 border-l-[15px] border-l-[#EB1E12] border-t-[15px] border-t-transparent border-b-[15px] border-b-transparent translate-x-full"></div>
              </div>
            </div>
          )}
        </div>
        {tourDetail?.is_most_popular && (
          <div className="absolute top-0 right-0 p-2 bg-[#C9E4A5] z-20 transition-all duration-300 md:group-hover:bg-[#B8D494]">
            <p className="text-[0.625rem] leading-3 font-semibold text-[#0B9444] lg:text-xs lg:leading-3">
              {t("Most Popular")}
            </p>
          </div>
        )}
      </div>

      <div className="bg-[#0B9444] text-white flex flex-col gap-4 p-6 flex-1 transition-all duration-300 md:group-hover:bg-[#0A8139]">
        <div className="flex flex-col gap-4 flex-1">
          <div className="flex items-center gap-2">
            <div className="flex bg-white rounded-[6px] py-[6px] px-2 items-center gap-2">
              <Image src={Images.iconDistance} alt="distance" width={16} height={16} />
              <p className="text-[10px] lg:text-xs leading-4 font-medium text-[#0C9443]">{tourDetail?.distance}</p>
            </div>
            <div className="flex bg-[#C9F0D9] rounded-[6px] py-[6px] px-2 items-center gap-2">
              <Image src={Images.iconDuration} alt="duration" width={16} height={16} />
              <p className="text-[10px] lg:text-xs leading-4 font-medium text-[#0C9443]">{tourDetail?.duration}</p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="text-base lg:text-lg leading-[28px] lg:leading-[30px] font-semibold transition-all duration-300 md:group-hover:text-[#C9E4A5]">
              {tourDetail?.name}
            </h3>
            <p className="text-sm lg:text-base leading-[25px] lg:leading-[27px] text-white/80 line-clamp-3 transition-all duration-300 md:group-hover:text-white">
              {tourDetail?.description}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between text-sm font-semibold mt-auto">
          <div className="flex gap-1 items-center">
            <p className="text-lg lg:text-xl leading-[17px] lg:leading-[19px] font-semibold transition-all duration-300 md:group-hover:scale-110 md:group-hover:text-[#C9E4A5]">
              {formatPrice(Number(priceMain), isVI ? "vi" : "en")}
            </p>
            <span className="text-xs lg:text-sm leading-[17px] lg:leading-[19px] text-white/80 transition-all duration-300 md:group-hover:text-white">
              / {priceUnit}
            </span>
          </div>
          <span className="text-sm lg:text-base leading-[25px] lg:leading-[27px] font-semibold underline transition-all duration-300 md:group-hover:translate-x-1 md:group-hover:text-[#C9E4A5]">
            {t("See details")}
          </span>
        </div>
      </div>
    </Link>
  )
}

export default TourCard
